import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import TextInput from '../TextInput';
import LockCopy from '../LockCopy';

import { purchaseFlowOperations } from '@evdy/web-redux/dist/lib/webPurchase/purchaseFlow';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import styles from './EmailForm.module.scss';

const { updateWebleadData, resetWebleadUpdated } = authUserOperations;

const {
  form: formClass,
  'lock-section': lockSectionClass,
  'privacy-section': privacySectionClass
} = styles;

const EmailForm = ({ moveToNextStep }) => {
  const dispatch = useDispatch();

  const { authUser = {}, purchaseFlow = {} } = useSelector(({ authUser, webPurchase }) => ({
    purchaseFlow: webPurchase?.purchaseFlow,
    authUser
  }));

  // assign most prioritized user data
  const { data: userData, webleadUpdated } = useMemo(() => {
    return !!authUser?.data?._id ? authUser : purchaseFlow;
  }, [authUser, purchaseFlow]);

  const { _id: userId, email, eFuneral, meta = {} } = userData ?? {};
  const { webLeadEmail } = meta || {};
  const selectedPackageName = eFuneral?.packageData?.packageName?.consumer;

  // after submit, navigate to next step
  useEffect(() => {
    if (webleadUpdated) {
      dispatch(purchaseFlowOperations?.resetUpdateWeblead());
      dispatch(resetWebleadUpdated());
      moveToNextStep();
    }
  }, [dispatch, moveToNextStep, webleadUpdated]);

  const emailRegex = /^\S+@\S+\.\S+$/;

  const initialValues = { email: email || webLeadEmail || '' };
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .matches(emailRegex, 'Please provide a valid email')
      .required('This is required!')
  });

  const onSubmit = e => {
    const updatedUserBody = {
      email: e?.email,
      eFuneral: {
        lastScreen: {
          flow: 'everdays',
          screen: 'emailForm',
          date: new Date().toISOString()
        }
      }
    };

    if (authUser?.data?._id) {
      dispatch(updateWebleadData(updatedUserBody));
    } else {
      dispatch(purchaseFlowOperations.updateWeblead(userId, updatedUserBody));
    }
  };

  return (
    <div className={formClass}>
      <h3>Let’s make sure you don’t lose your progress</h3>
      <Formik {...{ initialValues, validationSchema, onSubmit }} enableReinitialize validateOnMount>
        {({ dirty, isValid }) => (
          <Form>
            <TextInput
              autoFocus
              name="email"
              label="Email Address"
              placeholder="email@everdays.com"
            />
            <LockCopy copy="Everdays will never sell your personalized data to third parties" />
            <button disabled={!isValid} type="submit">
              Continue
            </button>
          </Form>
        )}
      </Formik>
      <p className={privacySectionClass}>
        By continuing, you agree to our <br />
        <a href="/privacy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>{' '}
        and{' '}
        <a href="/terms" target="_blank" rel="noopener noreferrer">
          Terms & Conditions
        </a>
      </p>
    </div>
  );
};

export default EmailForm;
